import axios from 'axios';
import type { AxiosInstance } from 'axios';
import type { ChatRequest, ChatResponse, ApiClient, OAuthTokenResponse } from '../types';
import type { StructuredComponent } from '../types/structuredComponents';
import { getApiConfig, generateBasicAuthHeader, generateUUIDHex } from '../config/apiCredentials';

// OneAI API response interface
interface OneAIResponse {
  choices?: Array<{
    message: {
      content: string;
      role: string;
    };
  }>;
  [key: string]: unknown;
}

class ChatbotApiClient implements ApiClient {
  private axiosInstance: AxiosInstance;
  private mockMode: boolean = false;
  private currentFaqStep: number = 1;
  // OAuth token management
  private accessToken: string | null = null;
  private oauthEndpoint: string;
  private oneaiEndpoint: string;
  private basicAuthHeader: string;
  
  // Session management
  private readonly sessionId: string = Math.floor(Math.random() * 100000).toString();
  private readonly userId: string = 'testuser@libertyglobal.com';

  constructor(
    options: { apiBaseUrl?: string; enableMockMode?: boolean; useProxy?: boolean } = {}
  ) {
    // Get configuration from centralized config
    const apiConfig = getApiConfig(options.apiBaseUrl, options.useProxy);

    // Set OAuth configuration
    this.oauthEndpoint = apiConfig.oauthEndpoint;
    this.oneaiEndpoint = apiConfig.oneaiEndpoint;
    this.basicAuthHeader = generateBasicAuthHeader();

    // Set mock mode based on options or config
    this.mockMode = options.enableMockMode ?? false;

    // Create axios instance with appropriate base URL
    let baseURL;
    let endpointPath;
    
    if (apiConfig.oneaiEndpoint.startsWith('/api/oneai')) {
      // For proxy endpoints, use empty baseURL and full path
      baseURL = '';
      endpointPath = apiConfig.oneaiEndpoint;
    } else if (apiConfig.oneaiEndpoint.startsWith('/OneAI/OneAssist/v1')) {
      baseURL = 'https://dev.aws1.apis.libertyglobal.com';
      endpointPath = apiConfig.oneaiEndpoint;
    } else {
      baseURL = apiConfig.baseURL;
      endpointPath = apiConfig.oneaiEndpoint;
    }
    
    // Store the correct endpoint path to use in API calls
    this.oneaiEndpoint = endpointPath;

    this.axiosInstance = axios.create({
      baseURL,
      // No timeout - wait indefinitely for API response
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Add response interceptor for error handling and token refresh
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Handle 401 Unauthorized - token expired
        if (error.response?.status === 401 && this.accessToken) {
          try {
            // Clear the expired token from memory and storage
            this.clearStoredToken();
            
            // Get a new token
            await this.authenticate();
            
            // Retry the original request with new token
            const originalRequest = error.config;
            originalRequest.headers['Authorization'] = `Bearer ${this.accessToken}`;
            return this.axiosInstance(originalRequest);
          } catch (authError) {
            throw new Error(`Token refresh failed: ${authError instanceof Error ? authError.message : 'Unknown error'}`);
          }
        }
        
        throw new Error(error.response?.data?.message || error.message || 'An error occurred');
      }
    );
    
    // Load existing token from session storage
    const storedToken = this.loadStoredToken();
    if (storedToken) {
      this.accessToken = storedToken.token;
      this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${this.accessToken}`;
    }
  }

  // OAuth token management methods
  private loadStoredToken(): { token: string; expiry: number } | null {
    try {
      const stored = sessionStorage.getItem('oneai_token');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if token is still valid (not expired)
        if (parsed.expiry && Date.now() < parsed.expiry) {
          return parsed;
        } else {
          // Token expired, remove it
          sessionStorage.removeItem('oneai_token');
        }
      }
    } catch {
      sessionStorage.removeItem('oneai_token');
    }
    return null;
  }

  private storeToken(token: string, expiresIn: number): void {
    try {
      const expiry = Date.now() + (expiresIn * 1000);
      const tokenData = { token, expiry };
      sessionStorage.setItem('oneai_token', JSON.stringify(tokenData));
    } catch {
      // Ignore storage errors
    }
  }

  private clearStoredToken(): void {
    this.accessToken = null;
    sessionStorage.removeItem('oneai_token');
    delete this.axiosInstance.defaults.headers.common['Authorization'];
  }

  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    // Use mock response only if mock mode is enabled
    if (this.mockMode) {
      console.log('🎭 Using mock mode');
      const mockResponse = await this.generateMockResponse();
      return {
        response: mockResponse.content,
        sessionId: this.sessionId,
        timestamp: new Date().toISOString()
      };
    }

    // Real API call
    try {
      console.log('🌐 Making real API call to OneAI');
      
      // Ensure we have a valid token
      if (!this.accessToken) {
        console.log('🔑 No access token, authenticating...');
        await this.authenticate();
      }

      // Prepare the API request
      const apiRequest = {
        model: "oneai",
        messages: [
          {
            role: "user",
            content: [
              {
                type: "text",
                text: request.message
              }
            ]
          }
        ],
        max_tokens: 50,
        temperature: 0.7
      };

      console.log('📤 Sending request:', apiRequest);

      // Make the API call
      const response = await this.axiosInstance.post(this.oneaiEndpoint, apiRequest, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'oneai_x_session_id': this.sessionId,
          'oneai_x_user_id': 'CUST-LG-102',
          'oneai_x_user_username': 'Jamie',
          'oneai_x_format_id': 'ONEASSIST_SUMMARY_GENUI_PROMPT',
          'oneai_x_trace_id' : generateUUIDHex()
        }
      });

      console.log('📥 Received response:', response.data);

      // Parse the response
      const parsedResponse = this.parseOneAIResponse(response.data);
      
      return parsedResponse;

    } catch (error) {
      console.error('❌ API call failed:', error);
      
      // If the error is 401, try to refresh token once
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        console.log('🔄 Token expired, refreshing...');
        try {
          await this.authenticate();
          
          // Retry the request with new token
          const apiRequest = {
            model: "oneai",
            messages: [
              {
                role: "user",
                content: [
                  {
                    type: "text",
                    text: request.message
                  }
                ]
              }
            ],
            max_tokens: 50,
            temperature: 0.7
          };

          const retryResponse = await this.axiosInstance.post(this.oneaiEndpoint, apiRequest, {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'oneai_x_session_id': this.sessionId,
              'oneai_x_user_id': 'CUST-IE-102',
              'oneai_x_user_username': 'Jamie',
              'oneai_x_format_id': 'ONEASSIST_SUMMARY_GENUI_PROMPT'
            }
          });

          return this.parseOneAIResponse(retryResponse.data);
          
        } catch (retryError) {
          console.error('❌ Retry failed:', retryError);
          throw new Error('Authentication failed. Please try again.');
        }
      }
      
      // For other errors, throw with a user-friendly message
      const errorMessage = axios.isAxiosError(error) 
        ? error.response?.data?.message || error.message 
        : 'An unexpected error occurred';
        
      throw new Error(`API Error: ${errorMessage}`);
    }
  }

  async authenticate(): Promise<string> {
  // Always use real OAuth authentication

    try {
      console.log('🔐 Making real OAuth authentication call');
      const response = await this.axiosInstance.post<OAuthTokenResponse>(this.oauthEndpoint, 
        'grant_type=client_credentials', 
        {
          headers: {
            'Authorization': this.basicAuthHeader,
            'Content-Type': 'application/x-www-form-urlencoded'
          }
          // No timeout - wait indefinitely for authentication
        }
      );

      this.accessToken = response.data.access_token;
      this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${this.accessToken}`;
      
      // Store token with expiration
      if (response.data.expires_in) {
        this.storeToken(this.accessToken, response.data.expires_in);
      }
      
      return this.accessToken;
    } catch (error) {
      console.error('❌ OAuth authentication failed:', error);
      
      // Don't fall back to mock mode - throw the error to let user know about the issue
      const errorMessage = axios.isAxiosError(error) 
        ? error.response?.data?.message || error.message 
        : 'Unknown error';
        
      throw new Error(`Authentication failed: ${errorMessage}`);
    }
  }

  async refreshToken(): Promise<string> {
    // For client credentials flow, refresh is the same as initial auth
    return this.authenticate();
  }

  uploadDocument(file: File): Promise<{ success: boolean; message: string }> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          message: `Successfully uploaded ${file.name}. The document has been processed and added to the knowledge base.`
        });
      }, 2000);
    });
  }

  getCurrentFaqStep(): number {
    return this.currentFaqStep;
  }

  isMockMode(): boolean {
    return this.mockMode;
  }

  private parseOneAIResponse(data: OneAIResponse): ChatResponse {
    let message = '';

    // Extract agent's reply from choices[0].message.content
    if (data?.choices && Array.isArray(data.choices) && data.choices.length > 0) {
      const choice = data.choices[0];
      if (choice?.message?.content && typeof choice.message.content === 'string') {
        message = choice.message.content;
      }
    }

    // Fallback if no message found
    if (!message) {
      message = 'I received your message but couldn\'t generate a proper response. Please try again.';
    }

    // Try to parse the content - it could be JSON wrapped in ```json``` blocks
    let parsedContent: unknown;
    try {
      // Remove JSON code block markers if present
      const cleanedMessage = message.replace(/^```json\s*/, '').replace(/\s*```$/, '').trim();
      parsedContent = JSON.parse(cleanedMessage);
    } catch {
      // If JSON parsing fails, try to extract from potential wrapper
      const jsonMessageMatch = message.match(/^\{[\s\n]*"message"\s*:\s*"([\s\S]*?)"[\s\n]*\}$/m);
      if (jsonMessageMatch) {
        message = jsonMessageMatch[1];
      } else {
        // Fallback: try to parse as JSON and extract message property
        try {
          const compact = message.replace(/\n/g, '').replace(/\s{2,}/g, ' ');
          const parsed = JSON.parse(compact);
          if (parsed && typeof parsed.message === 'string') {
            message = parsed.message;
          }
        } catch {
          // Not valid JSON, leave as is
        }
      }
      
      // Remove backslashes from escaped quotes
      message = message.replace(/\\"/g, '"');
      
      return {
        response: message, // Return as text string
        sessionId: this.sessionId,
        timestamp: new Date().toISOString()
      };
    }

    // Check if the parsed content is an array of structured components
    if (Array.isArray(parsedContent)) {
      // Validate that all items have componentType and content
      const isValidComponentArray = parsedContent.every(item => 
        item && typeof item === 'object' && 'componentType' in item && 'content' in item
      );
      
      if (isValidComponentArray) {
        return {
          response: {
            isMultiComponent: true,
            components: parsedContent
          },
          sessionId: this.sessionId,
          timestamp: new Date().toISOString()
        };
      }
    }

    // Check if it's a single structured component
    if (parsedContent && typeof parsedContent === 'object' && 'componentType' in parsedContent && 'content' in parsedContent) {
      return {
        response: parsedContent as StructuredComponent, // Return the structured component object
        sessionId: this.sessionId,
        timestamp: new Date().toISOString()
      };
    }

    // If we get here, treat as text response
    return {
      response: typeof parsedContent === 'string' ? parsedContent : JSON.stringify(parsedContent),
      sessionId: this.sessionId,
      timestamp: new Date().toISOString()
    };
  }

  // Generate comprehensive mock API responses matching OneAI format
  private async generateMockResponse(): Promise<{ content: string | StructuredComponent }> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 600));
    
    // Return comprehensive multi-component mock showing all structured components at once
    return {
      content: {
        isMultiComponent: true,
        components: [
          // //QRCode component
          // {
            // componentType: 'qrcode',
            // content: {
            //   header: 'Continue via our app anytime',
            //   imageLink: 'https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=app-download'
            // }
          // },
          // FAQ Answers Component
          {
            componentType: 'faqAnswersComponent',
            content: {
              title: 'Welcome to Virgin Media',
              message: 'Hello! Here\'s a comprehensive overview of all our available components and services. Below you\'ll see examples of all our structured UI components in action.'
            }
          },
          // Options Display Component
          {
            componentType: 'optionsDisplay',
            content: {
              options: ['Explore Plans', 'Check Coverage', 'View Bills', 'Get Support']
            }
          },
          // Current Plan Card
          {
            componentType: 'currentPlanCard',
            content: [{
              header: 'Current Plan',
              title: '500Mb Broadband + Loaded TV',
              description: '24-month Virgin Media plan with comprehensive services',
              options: [
                { key: 'Speed', value: '500Mb Broadband', icon: 'tick' },
                { key: 'TV Package', value: 'Loaded TV', icon: 'tick' },
                { key: 'Contract', value: '24-month plan', icon: 'tick' },
                { key: 'Monthly Cost', value: '€55', icon: 'tick' },
                { key: 'Status', value: 'Active', icon: 'tick' }
              ],
              actualAmount: '55',
              originalAmount: '65',
              currency: '€'
            }]
          },
          // Offers Comparison Card
          {
            componentType: 'offersComparisonCard',
            content: [{
              header: 'Plan Comparison',
              title: 'Virgin Media vs Competition',
              description: 'Compare our competitive pricing with other providers',
              options: [
                { bundleName: 'Virgin Media Offer', amount: '35', currency: '€' },
                { bundleName: 'Sky Ireland Offer', amount: '45', currency: '€' },
                { bundleName: 'BT Super Fast', amount: '49', currency: '€' },
                { bundleName: 'TalkTalk Fiber', amount: '52', currency: '€' }
              ],
              cta: 'Compare Plans'
            }]
          },
          // Offers Card
          {
            componentType: 'offersCard',
            content: [{
              header: 'Recommended Offer',
              title: 'Virgin Media 500Mb Broadband + Loaded TV',
              description: '12-month promotional plan with enhanced features',
              options: [
                { key: 'Speed', value: '500Mb Broadband' },
                { key: 'TV Package', value: 'Loaded TV' },
                { key: 'Contract', value: '12 months' },
                { key: 'WiFi Technology', value: 'Superior WiFi' },
                { key: 'Streaming Apps', value: 'Enhanced streaming' },
                { key: 'Bill Credit', value: '€100 credit' },
                { key: 'Setup Fees', value: 'No setup fees' },
                { key: 'Support', value: '24/7 customer support' }
              ],
              discountAmount: '20',
              actualAmount: '35',
              currency: '€',
              cta: 'Explore more options'
            }]
          },
          // Selected Plan Card
          {
            componentType: 'selectedPlanCard',
            "content": [
                            {
                                "header": "Selected Package",
                                "title": "Virgin Media 1Gb Broadband + Loaded TV",
                                "description": "High-speed internet and comprehensive TV package for your new address",
                                "options": [
                                    {
                                        "key": "Speed",
                                        "value": "1Gb Broadband"
                                    },
                                    {
                                        "key": "TV Package",
                                        "value": "Loaded TV"
                                    },
                                    {
                                        "key": "New Address",
                                        "value": "107 Quay Street, Sligo, F91 D3E5"
                                    }
                                ],
                                "arrivalText": "Installation will be scheduled",
                                "arrivalSubText": "Based on your move date",
                                "paymentDetails": "Monthly billing will commence after installation",
                                "discountAmount": "0",
                                "actualAmount": "50",
                                "currency": "€"
                            }
                        ]
          },
          // Usage Card
          {
            componentType: 'usageCard',
            content: {
              title: 'Your 2025 Usage Highlights',
              metrics: [
                {
                  icon: 'clock' as const,
                  value: '365 days',
                  description: 'Using our services'
                },
                {
                  icon: 'devices' as const,
                  value: '12 devices',
                  description: 'Connected to network'
                },
                {
                  icon: 'video' as const,
                  value: '2,847 hours',
                  description: 'Entertainment streamed'
                },
                {
                  icon: 'data' as const,
                  value: '2.5TB',
                  description: 'Data downloaded'
                }
              ]
            }
          },
          // Bill Summary
          {
            componentType: 'billSummary',
            content: [{
              header: 'Your Next Bill',
              dueAmount: '35.00',
              currency: '€',
              month: 'Oct',
              regularCost: '55.00',
              subscriptionExtras: '0.00',
              proRataCost: '0.00',
              packageDetail: 'Virgin Media 500Mb Broadband + Loaded TV with promotional discount'
            }]
          },
          // Options Summary Card
          {
            componentType: 'optionsSummaryCard',
            content: {
              title: 'Benefits of Staying',
              options: [
                { text: 'Access to exclusive loyalty offers', checked: true },
                { text: 'No early cancellation fees', checked: true },
                { text: 'Fast service transfer support', checked: true },
                { text: 'Priority customer support', checked: true }
              ]
            }
          },
          // Order Arrival Card
          {
            componentType: 'orderArrivalCard',
            content: [{
              arrivalDate: 'Friday, March 10th',
              arrivalTime: '8am - 12pm'
            }]
          }
        ]
      }
    };
  }

  // Session management methods
  getSessionId(): string {
    return this.sessionId;
  }

  startNewOneAISession(): void {
    // Session ID generation is handled in constructor
  }

  getUserId(): string {
    return this.userId;
  }
}

export default ChatbotApiClient;
